import type { CabinetSummary } from "@planerius/contracts/cabinet"
import { type ReactNode, useCallback, useEffect, useState } from "react"

import { useHasPermission } from "../../access/use-access"
import { type ApiProblem, isApiProblem } from "../../api/problem"
import { fetchSummary } from "../../api/summary.api"
import { formatDateLong, formatTimeOfDay, shiftDate, todayInZone } from "../../lib/format"
import { countOf } from "../../lib/plural"
import { ProblemScreen, Skeleton } from "../../shell/states"
import { useOnline } from "../../shell/use-online"
import { Button, EmptyState } from "../../ui"
import {
  BOOKING_ONLY_TEXT,
  bookingsCard,
  CARD_FAILED,
  type CardView,
  dayLabel,
  FIRST_DAY_TEXT,
  FIRST_DAY_TITLE,
  freeSlotsCard,
  isFirstDay,
  isQuietDay,
  loadCard,
  lossesCard,
  NO_BRANCHES_TEXT,
  NO_BRANCHES_TITLE,
  QUIET_DAY_TEXT,
  QUIET_DAY_TITLE,
  revenueCard,
} from "./summary-model"
import styles from "./summary-screen.module.css"

/**
 * 5-В1 «Сводка» — /summary.
 *
 * ПЕРВЫЙ ЭКРАН ВЛАДЕЛЬЦА, И ПОТОМУ САМЫЙ ОПАСНЫЙ. Его открывают между двумя клиентами,
 * на телефоне, и решения по нему принимают сразу: кому звонить, кого догружать, с кем
 * разговаривать про неявки. Экран, который ошибся молча, дороже экрана, который
 * не открылся.
 *
 * ДЕНЬ ЖИВЁТ В АДРЕСЕ, А НЕ В СОСТОЯНИИ ЭКРАНА. «Завтра» — это другой запрос, а не
 * другая подпись: пока переключатель менял только `useState`, он переписывал дату
 * в шапке, оставляя под ней сегодняшние цифры. Владелец читал «Чт, 24 сентября» и
 * вчерашнюю выручку. Заодно ссылка на день пересылается в переписке и открывается
 * тем же экраном.
 *
 * ШЕСТЬ КАРТОЧЕК СЧИТАЮТСЯ И ОТКАЗЫВАЮТ ОТДЕЛЬНО. Падение одной не уносит остальные
 * пять: на её месте стоит «Не посчиталась — остальное актуально» и «Повторить».
 * Ноль вместо отказа запрещён: «выручка 0 ₽» читается как разорение.
 *
 * КАРКАС БЕЗ НУЛЕЙ. Пока цифры не пришли, на их месте серые полосы, а не «0». Ноль,
 * показанный на долю секунды, владелец успевает прочитать — и звонит в поддержку.
 */
export function SummaryScreen(props: {
  readonly search: { readonly date?: string | undefined; readonly branch?: string | undefined }
  readonly onSearchChange: (next: { date?: string | undefined; branch?: string | undefined }) => void
  readonly onOpenLoad: () => void
  readonly onOpenLosses: () => void
  readonly onOpenBooked: () => void
  readonly onOpenClients: () => void
}): ReactNode {
  const online = useOnline()
  // «База клиентов» ведёт в раздел, закрытый правом. У бухгалтера и наблюдателя его нет,
  // а сводка для них — первый экран после входа: ссылка в отказ была бы первым, что они
  // нажмут.
  const mayOpenClients = useHasPermission("clients.list.view")
  const [summary, setSummary] = useState<CabinetSummary | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)

  const { date, branch } = props.search

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const next = await fetchSummary(
          {
            ...(date === undefined ? {} : { date }),
            ...(branch === undefined ? {} : { branchId: branch }),
          },
          signal,
        )
        if (signal?.aborted === true) return
        setSummary(next)
        setProblem(null)
      } catch (error) {
        if (signal?.aborted === true) return
        if (!isApiProblem(error)) throw error
        setProblem(error)
      }
    },
    [date, branch],
  )

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  // Отказ ВСЕГО запроса — это другое дело, чем отказ карточки: показывать нечего.
  if (problem !== null && summary === null) {
    return <ProblemScreen problem={problem} onRetry={() => void load()} />
  }

  if (summary === null) {
    return (
      <div className={styles["screen"]}>
        <div className={styles["skeletons"]} aria-busy="true" aria-live="polite">
          {[0, 1, 2, 3, 4, 5].map((row) => (
            <Skeleton key={row} width="100%" height={92} radius={12} />
          ))}
        </div>
      </div>
    )
  }

  if (summary.mode === "booking_only") {
    return (
      <div className={styles["screen"]}>
        <h1 className={styles["title"]}>Сводка</h1>
        <p className={styles["caption"]}>{BOOKING_ONLY_TEXT}</p>
      </div>
    )
  }

  const zone = summary.scope.timezone
  const today = todayInZone(zone)
  const tomorrow = shiftDate(today, 1)
  const day = dayLabel(summary.date, zone)
  const showingTomorrow = summary.date === tomorrow
  const branchLabel =
    summary.scope.allBranches && summary.scope.branches.length > 1
      ? `Все филиалы · ${countOf(summary.scope.branches.length, "точка", "точки", "точек")}`
      : (summary.scope.branches[0]?.name ?? "Филиал")

  const cards: readonly (CardView & { readonly onOpen?: () => void })[] = [
    bookingsCard(summary.today, "today", day),
    bookingsCard(summary.tomorrow, "tomorrow", day),
    { ...loadCard(summary.load, day), onOpen: props.onOpenLoad },
    revenueCard(summary.revenue, day),
    { ...lossesCard(summary.losses, day), onOpen: props.onOpenLosses },
    freeSlotsCard(summary.freeSlots, zone),
  ]

  const header = (
    <>
      <div className={styles["head"]}>
        <h1 className={styles["title"]}>Сводка</h1>
        <span className={styles["subtitle"]}>
          {formatDateLong(summary.date, zone)} · {branchLabel}
        </span>
      </div>

      {online ? null : (
        // Время сборки, а не «обновлено только что»: в офлайне человек должен знать,
        // насколько старым числам он верит.
        <div className={styles["offline"]} role="status">
          Нет связи. Данные на {formatTimeOfDay(summary.generatedAt, zone)}
        </div>
      )}

      <div className={styles["chips"]} role="group" aria-label="День сводки">
        <Button
          size="sm"
          variant={showingTomorrow ? "secondary" : "primary"}
          // Выбор объявляется не только цветом: незрячему человеку цвет не говорит
          // ничего, а день — это то, о чём весь экран.
          aria-pressed={!showingTomorrow}
          onClick={() => {
            props.onSearchChange({ date: undefined })
          }}
        >
          Сегодня
        </Button>
        <Button
          size="sm"
          variant={showingTomorrow ? "primary" : "secondary"}
          aria-pressed={showingTomorrow}
          onClick={() => {
            props.onSearchChange({ date: tomorrow })
          }}
        >
          Завтра
        </Button>
      </div>
    </>
  )

  if (summary.scope.branches.length === 0) {
    return (
      <div className={styles["screen"]}>
        <div className={styles["head"]}>
          <h1 className={styles["title"]}>Сводка</h1>
        </div>
        <EmptyState title={NO_BRANCHES_TITLE} text={NO_BRANCHES_TEXT} />
      </div>
    )
  }

  return (
    <div className={styles["screen"]}>
      {header}

      {isFirstDay(summary) ? (
        <EmptyState title={FIRST_DAY_TITLE} text={FIRST_DAY_TEXT} />
      ) : isQuietDay(summary) ? (
        // Пустой день РАБОТАЮЩЕГО салона — это ответ про день, а не про салон.
        <EmptyState title={QUIET_DAY_TITLE} text={QUIET_DAY_TEXT} />
      ) : (
        <div className={styles["cards"]}>
          {cards.map((card) =>
            card.failed ? (
              <div key={card.title} className={styles["cardFailed"]} role="status">
                <span className={styles["cardTitle"]}>{card.title}</span>
                <span className={styles["failure"]}>{CARD_FAILED}</span>
                <Button size="sm" variant="secondary" onClick={() => void load()}>
                  Повторить
                </Button>
              </div>
            ) : card.onOpen === undefined ? (
              <div key={card.title} className={styles["card"]}>
                <CardBody card={card} />
              </div>
            ) : (
              <button key={card.title} type="button" className={styles["cardLink"]} onClick={card.onOpen}>
                <CardBody card={card} />
                <span className={styles["arrow"]}>Разобрать ›</span>
              </button>
            ),
          )}
        </div>
      )}

      <div className={styles["links"]}>
        <button type="button" className={styles["link"]} onClick={props.onOpenBooked}>
          <span>Что уже записано</span>
          <span className={styles["linkTail"]}>7 дней ›</span>
        </button>
        {mayOpenClients ? (
          <button type="button" className={styles["link"]} onClick={props.onOpenClients}>
            <span>База клиентов</span>
            <span className={styles["linkTail"]}>поиск ›</span>
          </button>
        ) : null}
      </div>
    </div>
  )
}

function CardBody(props: { readonly card: CardView }): ReactNode {
  const { card } = props
  const deltaClass =
    card.deltaTone === "good"
      ? styles["deltaGood"]
      : card.deltaTone === "bad"
        ? styles["deltaBad"]
        : styles["deltaFlat"]
  return (
    <>
      <span className={styles["cardTitle"]}>{card.title}</span>
      <span className={styles["valueRow"]}>
        <span className={styles["value"]}>{card.value ?? "—"}</span>
        {card.delta === null ? null : <span className={deltaClass}>{card.delta}</span>}
      </span>
      {card.caption === null ? null : <span className={styles["caption"]}>{card.caption}</span>}
    </>
  )
}
